import type { Editor } from '@tiptap/core'
import { useEditorState } from '@tiptap/react'
import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
  IconBold,
  IconClearFormatting,
  IconCode,
  IconEyeQuestion,
  IconH1,
  IconH2,
  IconH3,
  IconItalic,
  IconLine,
  IconLink,
  IconList,
  IconListNumbers,
  IconPhoto,
  IconQuote,
  IconBraces,
  IconStrikethrough,
  IconSubscript,
  IconSuperscript,
  IconUnderline,
  IconArrowBackUp,
  IconArrowForwardUp,
} from '@tabler/icons-react'
import { useId, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'
import {
  EDITOR_FONT_FAMILIES,
  EDITOR_HIGHLIGHT_COLORS,
  EDITOR_TEXT_COLORS,
} from '~shared/formatting-allowlist'

function insertSpoiler(editor: Editor) {
  editor
    .chain()
    .focus()
    .insertContent({
      type: 'details',
      content: [
        {
          type: 'detailsSummary',
          content: [{ type: 'text', text: 'Spoiler' }],
        },
        {
          type: 'detailsContent',
          content: [{ type: 'paragraph' }],
        },
      ],
    })
    .run()
}

function ToolbarDivider() {
  return <div className="hidden h-7 w-px shrink-0 self-center bg-nk-fog sm:block" aria-hidden />
}

function ToolIconBtn({
  onClick,
  pressed,
  disabled,
  label,
  children,
}: {
  onClick: () => void
  pressed?: boolean
  disabled?: boolean
  label: string
  children: ReactNode
}) {
  return (
    <Button
      type="button"
      variant={pressed ? 'primary' : 'outline'}
      size="icon"
      disabled={disabled}
      aria-pressed={pressed}
      aria-label={label}
      title={label}
      className="shrink-0"
      onMouseDown={e => e.preventDefault()}
      onClick={() => onClick()}
    >
      {children}
    </Button>
  )
}

export function RichEditorToolbar({ editor }: { editor: Editor }) {
  const uid = useId()
  const fontId = `${uid}-font`
  const colorId = `${uid}-color`

  const snap = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (!ed) return null
      const ts = ed.getAttributes('textStyle') as { fontFamily?: string; color?: string }
      const hi = ed.getAttributes('highlight') as { color?: string }
      return {
        bold: ed.isActive('bold'),
        italic: ed.isActive('italic'),
        underline: ed.isActive('underline'),
        strike: ed.isActive('strike'),
        code: ed.isActive('code'),
        sub: ed.isActive('subscript'),
        sup: ed.isActive('superscript'),
        link: ed.isActive('link'),
        bullet: ed.isActive('bulletList'),
        ordered: ed.isActive('orderedList'),
        quote: ed.isActive('blockquote'),
        codeBlock: ed.isActive('codeBlock'),
        h1: ed.isActive('heading', { level: 1 }),
        h2: ed.isActive('heading', { level: 2 }),
        h3: ed.isActive('heading', { level: 3 }),
        alignLeft: ed.isActive({ textAlign: 'left' }),
        alignCenter: ed.isActive({ textAlign: 'center' }),
        alignRight: ed.isActive({ textAlign: 'right' }),
        alignJustify: ed.isActive({ textAlign: 'justify' }),
        highlight: ed.isActive('highlight'),
        highlightColor: hi?.color as string | undefined,
        fontFamily: (ts?.fontFamily as string | undefined) ?? '',
        textColor: (ts?.color as string | undefined) ?? '',
        canUndo: ed.can().undo(),
        canRedo: ed.can().redo(),
      }
    },
  })

  if (!snap) return null

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className="flex max-h-[min(40vh,22rem)] flex-wrap items-center gap-1.5 overflow-y-auto border-b-2 border-nk-fog bg-nk-sand px-2 py-2 sm:max-h-none"
    >
      <div className="flex flex-wrap items-center gap-1">
        <ToolIconBtn
          label="Undo"
          disabled={!snap.canUndo}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <IconArrowBackUp className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Redo"
          disabled={!snap.canRedo}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <IconArrowForwardUp className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
      </div>

      <ToolbarDivider />

      <div className="flex flex-wrap items-center gap-1">
        <ToolIconBtn
          label="Bold"
          pressed={snap.bold}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <IconBold className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Italic"
          pressed={snap.italic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <IconItalic className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Underline"
          pressed={snap.underline}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <IconUnderline className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Strikethrough"
          pressed={snap.strike}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <IconStrikethrough className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Inline code"
          pressed={snap.code}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <IconCode className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Subscript"
          pressed={snap.sub}
          onClick={() => editor.chain().focus().toggleSubscript().run()}
        >
          <IconSubscript className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Superscript"
          pressed={snap.sup}
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
        >
          <IconSuperscript className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
      </div>

      <ToolbarDivider />

      <div className="flex flex-wrap items-center gap-1">
        <ToolIconBtn
          label="Heading 1"
          pressed={snap.h1}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <IconH1 className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Heading 2"
          pressed={snap.h2}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <IconH2 className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Heading 3"
          pressed={snap.h3}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <IconH3 className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
      </div>

      <ToolbarDivider />

      <div className="flex flex-wrap items-center gap-1">
        <ToolIconBtn
          label="Align left"
          pressed={snap.alignLeft}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          <IconAlignLeft className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Align center"
          pressed={snap.alignCenter}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          <IconAlignCenter className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Align right"
          pressed={snap.alignRight}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          <IconAlignRight className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Justify"
          pressed={snap.alignJustify}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        >
          <IconAlignJustified className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
      </div>

      <ToolbarDivider />

      <div className="flex flex-wrap items-center gap-1">
        <ToolIconBtn
          label="Bullet list"
          pressed={snap.bullet}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <IconList className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Numbered list"
          pressed={snap.ordered}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <IconListNumbers className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Quote"
          pressed={snap.quote}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <IconQuote className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Code block"
          pressed={snap.codeBlock}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          <IconBraces className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Horizontal rule"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <IconLine className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
      </div>

      <ToolbarDivider />

      <div className="flex flex-wrap items-center gap-1">
        <ToolIconBtn
          label="Link"
          pressed={snap.link}
          onClick={() => {
            const prev = window.prompt('Link URL (https:// or mailto:)')
            if (!prev) return
            editor.chain().focus().extendMarkRange('link').setLink({ href: prev }).run()
          }}
        >
          <IconLink className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn
          label="Image from URL"
          onClick={() => {
            const src = window.prompt('Image URL (https only)')
            if (!src) return
            editor.chain().focus().setImage({ src }).run()
          }}
        >
          <IconPhoto className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
        <ToolIconBtn label="Insert spoiler" onClick={() => insertSpoiler(editor)}>
          <IconEyeQuestion className="h-4 w-4" aria-hidden />
        </ToolIconBtn>
      </div>

      <ToolbarDivider />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5">
          <label htmlFor={fontId} className="sr-only">
            Font
          </label>
          <select
            id={fontId}
            className={cn(
              'h-9 max-w-[11rem] border-2 border-nk-fog bg-white px-2 text-[12px] font-bold text-nk-navy',
              'outline-none focus-visible:ring-2 focus-visible:ring-nk-sea focus-visible:ring-offset-2',
            )}
            value={snap.fontFamily}
            onChange={e => {
              const v = e.target.value
              const chain = editor.chain().focus()
              if (!v) chain.unsetFontFamily().run()
              else chain.setFontFamily(v).run()
            }}
          >
            {EDITOR_FONT_FAMILIES.map(f => (
              <option key={f.label} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1.5">
          <label htmlFor={colorId} className="sr-only">
            Text color
          </label>
          <select
            id={colorId}
            className={cn(
              'h-9 max-w-[9rem] border-2 border-nk-fog bg-white px-2 text-[12px] font-bold text-nk-navy',
              'outline-none focus-visible:ring-2 focus-visible:ring-nk-sea focus-visible:ring-offset-2',
            )}
            value={snap.textColor}
            onChange={e => {
              const v = e.target.value
              const chain = editor.chain().focus()
              if (!v) chain.unsetColor().run()
              else chain.setColor(v).run()
            }}
          >
            {EDITOR_TEXT_COLORS.map(c => (
              <option key={c.label} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-0.5" role="group" aria-label="Highlight color">
          {EDITOR_HIGHLIGHT_COLORS.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              title={label}
              aria-label={`Highlight ${label}`}
              aria-pressed={snap.highlight && snap.highlightColor === value}
              className={cn(
                'size-8 shrink-0 border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nk-sea focus-visible:ring-offset-2',
                snap.highlight && snap.highlightColor === value ? 'border-nk-navy' : 'border-nk-fog',
              )}
              style={{ backgroundColor: value }}
              onMouseDown={e => e.preventDefault()}
              onClick={() => {
                editor.chain().focus().toggleHighlight({ color: value }).run()
              }}
            />
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0 px-2 text-[11px]"
            title="Remove highlight"
            onMouseDown={e => e.preventDefault()}
            onClick={() => editor.chain().focus().unsetHighlight().run()}
          >
            Clear
          </Button>
        </div>
      </div>

      <ToolbarDivider />

      <ToolIconBtn
        label="Clear formatting"
        onClick={() =>
          editor.chain().focus().unsetAllMarks().unsetHighlight().unsetTextAlign().run()
        }
      >
        <IconClearFormatting className="h-4 w-4" aria-hidden />
      </ToolIconBtn>
    </div>
  )
}
